<template>
  <v-sheet rounded :elevation="4" :style="{ fontFamily: themeFont }">
    <div
      class="top-bar py-2 px-4 font-weight-black text-uppercase"
      :style="{ backgroundColor: topBarBackgroundColor }"
    >
      <div
        class="progress-bar"
        v-if="errors.length == 0"
        :style="progressBarStyle"
      ></div>
      <div class="top-bar-text">{{ fileName }}</div>
    </div>
    <div
      class="content pa-4 d-flex justify-space-between align-center"
      :class="{ mobile: isMobile }"
    >
      <div
        :class="{ 'mobile-size': isMobile }"
        class="image-container flex-shrink-0"
      >
        <img class="image" v-if="thumbnailUrl" :src="thumbnailUrl" />
        <div
          :class="{ 'mobile-size': isMobile }"
          class="file-extension-thumbnail rounded"
          :style="{ borderColor: primaryColor, color: primaryColor }"
          v-else
        >
          {{ extension }}
        </div>
      </div>
      <sui-form class="fields-container">
        <slot name="field1"></slot>
        <slot name="field2"></slot>
        <slot name="field3"></slot>
      </sui-form>
      <div class="actions-container">
        <sui-button
          icon="suus-simple-remove"
          @click="$emit('remove')"
        ></sui-button>
      </div>
    </div>
    <template v-if="errors.length > 0">
      <v-divider></v-divider>
      <div class="error-area pa-4" :style="{ color: errorColor }">
        Es sind folgende Fehler aufgetreten:
        <ul>
          <li v-for="err in errors">{{ err }}</li>
        </ul>
      </div>
    </template>
  </v-sheet>
</template>

<script lang="ts">
import { defineComponent, useSlots } from "vue"
import theme from "constants/theme"

export default defineComponent({
  name: "DriveUploadCard",
  props: {
    fileName: {
      type: String,
    },
    success: {
      type: Boolean,
    },
    errors: {
      type: Array,
      default() {
        return []
      },
    },
    thumbnailUrl: {
      type: String,
    },
    fileExtension: {
      type: String,
    },
    progress: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    topBarBackgroundColor(): string {
      // This now returns a constant or status-based color without progress consideration
      if (this.errors.length > 0) {
        return theme.colors.light.error
      } else if (this.success) {
        return theme.colors.light.highlight
      } else {
        return theme.colors.light.primary
      }
    },
    progressBarStyle(): Record<string, string | number> {
      return {
        width: `${this.progress}%`,
        backgroundColor: theme.colors.light.highlight,
        transition: "width 0.5s ease-in-out",
        height: "100%",
      }
    },
    primaryColor() {
      return theme.colors.light.primary
    },
    themeFont() {
      return theme.typography.fontFamily
    },
    errorColor() {
      return theme.colors.light.error
    },
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
    extension(): string {
      const fileNameSplitted = this.fileName.split(".")
      const lastElement = fileNameSplitted[fileNameSplitted.length - 1]
      if (fileNameSplitted.length <= 1 || lastElement.length > 3) {
        return "FILE"
      }
      return lastElement.toUpperCase()
    },
  },
  setup() {
    const slots = useSlots()
    return {
      slots,
    }
  },
})
</script>

<style scoped>
.top-bar {
  font-size: 12px;
  color: white;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  position: relative;
}

.image-container {
  width: 48px;
  height: 48px;
  overflow: hidden;
  border-radius: 4px;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-extension-thumbnail {
  width: 48px;
  height: 48px;
  border-width: 2px;
  border-style: solid;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: bold;
}

.mobile-size {
  width: 100px;
  height: 100px;
  font-size: 24px;
}

.fields-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-left: 8px;
  padding-right: 8px;
}

.content.mobile {
  flex-direction: column;
  align-items: center;
}

.mobile .fields-container {
  flex-direction: column;
  align-items: stretch;
  margin-top: 24px;
  margin-bottom: 24px;
}

.progress-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  transition: width 0.5s ease-in-out;
  z-index: 1;
}

.top-bar-text {
  position: relative;
  z-index: 2;
}
</style>

<style>
.content .fields-container > * {
  margin-right: 8px !important;
  maring-left: 8px !important;
}

.content.mobile .fields-container > * {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
}
</style>
